class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.currentUrl = '';
    this.drawerCloseTimer = null;
    this.requestId = 0;
    this.resizeObserver = null;
    this.onClick = this.handleClick.bind(this);
    this.onChange = this.handleInput.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onFocusout = this.handlePriceFieldFocusout.bind(this);
    this.onInput = this.handleInput.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onPointerdown = this.handlePointerdown.bind(this);
    this.onPopstate = this.handlePopstate.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.toolbarScrollFrame = null;
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.sectionId = this.dataset.sectionId;
    this.currentUrl = this.cleanUrl(window.location.href).toString();
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('focusout', this.onFocusout);
    this.addEventListener('input', this.onInput);
    this.addEventListener('pointerdown', this.onPointerdown);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopstate);
    window.addEventListener('scroll', this.onScroll, {passive: true});
    this.resizeObserver = new ResizeObserver(() => this.renderPriceRanges());
    this.resizeObserver.observe(this);
    this.initializeUi();
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('focusout', this.onFocusout);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('pointerdown', this.onPointerdown);
    this.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopstate);
    window.removeEventListener('scroll', this.onScroll);
    window.cancelAnimationFrame(this.toolbarScrollFrame);
    this.initialized = false;
  }

  initializeUi() {
    this.querySelectorAll('[data-filter-drawer-accordion-trigger]').forEach((trigger) => {
      const body = this.getAccordionBody(trigger);
      if (!body) {
        return;
      }

      const expanded = trigger.getAttribute('aria-expanded') !== 'false';
      trigger.classList.toggle('accordion-is-open', expanded);
      trigger.setAttribute('aria-expanded', String(expanded));
    });

    this.renderPriceRanges();
    this.updateToolbarStickyState();
  }

  handleScroll() {
    if (this.toolbarScrollFrame) {
      return;
    }

    this.toolbarScrollFrame = window.requestAnimationFrame(() => {
      this.toolbarScrollFrame = null;
      this.updateToolbarStickyState();
    });
  }

  updateToolbarStickyState() {
    const toolbar = this.querySelector('.collection-toolbar');
    if (!toolbar) {
      return;
    }

    const stickyTop = Number.parseFloat(window.getComputedStyle(toolbar).top) || 0;
    const toolbarBounds = toolbar.getBoundingClientRect();
    const isStuck = toolbarBounds.bottom > stickyTop && toolbarBounds.top <= stickyTop + 1;

    toolbar.classList.toggle('is-stuck', isStuck);
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const sortToggle = event.target.closest('[data-collection-sort-toggle]');
    if (sortToggle && this.contains(sortToggle)) {
      event.preventDefault();
      this.toggleSortMenu(sortToggle);
      return;
    }

    const sortOption = event.target.closest('[data-collection-sort-option]');
    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      const sortValue = sortOption.dataset.sortValue;
      if (!sortValue) {
        return;
      }

      const url = this.cleanUrl(window.location.href);
      url.searchParams.set('sort_by', sortValue);
      url.searchParams.delete('page');
      this.closeSortMenu();
      this.navigate(url, {focusSortToggle: true});
      return;
    }

    const openButton = event.target.closest('[data-filter-drawer-open]');
    if (openButton && this.contains(openButton)) {
      event.preventDefault();
      this.closeSortMenu();
      this.openDrawer();
      return;
    }

    const closeButton = event.target.closest('[data-filter-drawer-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.closeDrawer({restoreFocus: true});
      return;
    }

    const accordionTrigger = event.target.closest('[data-filter-drawer-accordion-trigger]');
    if (accordionTrigger && this.contains(accordionTrigger)) {
      event.preventDefault();
      this.toggleAccordion(accordionTrigger);
      return;
    }

    const showMoreButton = event.target.closest('[data-show-more]');
    if (showMoreButton && this.contains(showMoreButton)) {
      event.preventDefault();
      showMoreButton.previousElementSibling?.querySelectorAll('.is-hidden').forEach((option) => option.classList.remove('is-hidden'));
      showMoreButton.classList.add('is-hidden');
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link || !this.shouldHandleLink(link) || !this.isPlainLeftClick(event)) {
      return;
    }

    const url = this.cleanUrl(link.href);
    if (url.origin !== window.location.origin) {
      return;
    }

    event.preventDefault();
    const linkIsInsideDrawer = Boolean(link.closest('[data-filter-drawer-form]'));
    if (linkIsInsideDrawer) {
      this.closeDrawer({immediate: true, restoreFocus: false});
    }

    const shouldScrollToCollectionStart =
      link.matches(
        '.filters__drawer-clear, .active__filters__remove, .active__filters__clear, [data-collection-ajax-reset]',
      ) || Boolean(link.closest('.pagination'));

    this.navigate(url, {
      focusFilterTrigger: linkIsInsideDrawer,
      scrollToCollectionStart: shouldScrollToCollectionStart,
    });
  }

  handleDocumentClick(event) {
    if (!(event.target instanceof Element) || event.target.closest('[data-collection-sort]')) {
      return;
    }

    this.closeSortMenu();
  }

  handleInput(event) {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const priceRange = event.target.closest('.filter__price')?.querySelector('[data-price-range]');
    if (!priceRange) {
      return;
    }

    if (event.target.matches('[data-field-price-min], [data-field-price-max]')) {
      this.updateRangeFromFields(priceRange, event.target, true);
    } else if (event.target.matches('[data-price-range-min], [data-price-range-max]')) {
      this.updateFieldsFromRanges(priceRange, event.target);
    }
  }

  handlePriceFieldFocusout(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-field-price-min], [data-field-price-max]')) {
      return;
    }

    const priceRange = event.target.closest('.filter__price')?.querySelector('[data-price-range]');
    if (priceRange) {
      this.updateRangeFromFields(priceRange, event.target, true);
    }
  }

  handlePointerdown(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const priceRange = event.target.closest('[data-price-range]');
    if (!priceRange || event.target.closest('input[type="range"]')) {
      return;
    }

    const parts = this.getPriceRangeParts(priceRange);
    if (!parts) {
      return;
    }

    const bounds = priceRange.getBoundingClientRect();
    const position = Math.min(1, Math.max(0, (event.clientX - 10 - bounds.left) / Math.max(1, bounds.width - 20)));
    const value = Math.round(parts.minimum + position * (parts.maximum - parts.minimum));
    const lowerDistance = Math.abs(value - Number(parts.minRange.value));
    const upperDistance = Math.abs(value - Number(parts.maxRange.value));
    const changedRange = lowerDistance <= upperDistance ? parts.minRange : parts.maxRange;

    changedRange.value = String(value);
    this.updateFieldsFromRanges(priceRange, changedRange);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    const openSortToggle = this.querySelector('[data-collection-sort-toggle][aria-expanded="true"]');
    if (openSortToggle) {
      this.closeSortMenu({restoreFocus: true});
      return;
    }

    const form = this.querySelector('[data-filter-drawer-form]');
    if (form && !form.hidden) {
      this.closeDrawer({restoreFocus: true});
    }
  }

  handlePopstate() {
    this.closeSortMenu();
    this.closeDrawer({immediate: true, restoreFocus: false});
    this.navigate(window.location.href, {updateHistory: false});
  }

  handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-filter-drawer-form]')) {
      return;
    }

    event.preventDefault();
    this.closeDrawer({immediate: true, restoreFocus: false});
    this.navigate(this.getFormUrl(form), {focusFilterTrigger: true, scrollToCollectionStart: true});
  }

  openDrawer() {
    const form = this.querySelector('[data-filter-drawer-form]');
    const backdrop = this.querySelector('.filters__drawer-backdrop');
    const openButton = this.querySelector('[data-filter-drawer-open]');
    if (!form || !backdrop || !openButton) {
      return;
    }

    window.clearTimeout(this.drawerCloseTimer);
    backdrop.hidden = false;
    form.hidden = false;
    this.querySelector('.collection-toolbar')?.classList.add('is-drawer-open');
    openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('filter-drawer-open');
    const scrollableContent = form.querySelector('.filters__drawer-content') || form;
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollableContent}));

    window.requestAnimationFrame(() => {
      form.classList.add('is-open');
      this.renderPriceRanges();
      form.querySelector('[data-filter-drawer-close]')?.focus({preventScroll: true});
    });
  }

  toggleSortMenu(toggle) {
    const menu = this.getSortMenu(toggle);
    if (!menu) {
      return;
    }

    const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';
    this.closeSortMenu();
    toggle.setAttribute('aria-expanded', String(shouldOpen));
    menu.hidden = !shouldOpen;

    if (shouldOpen) {
      menu.querySelector('[aria-checked="true"]')?.focus();
    }
  }

  closeSortMenu({restoreFocus = false} = {}) {
    const openToggle = this.querySelector('[data-collection-sort-toggle][aria-expanded="true"]');
    this.querySelectorAll('[data-collection-sort-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-collection-sort-menu]').forEach((menu) => {
      menu.hidden = true;
    });

    if (restoreFocus) {
      openToggle?.focus();
    }
  }

  getSortMenu(toggle) {
    const menuId = toggle.getAttribute('aria-controls');
    return menuId ? this.querySelector(`[data-collection-sort-menu][id="${menuId}"]`) : null;
  }

  closeDrawer({immediate = false, restoreFocus = false} = {}) {
    const form = this.querySelector('[data-filter-drawer-form]');
    const backdrop = this.querySelector('.filters__drawer-backdrop');
    const openButton = this.querySelector('[data-filter-drawer-open]');
    if (!form || !backdrop || !openButton) {
      return;
    }

    window.clearTimeout(this.drawerCloseTimer);
    form.classList.remove('is-open');
    openButton.setAttribute('aria-expanded', 'false');

    if (document.documentElement.classList.contains('filter-drawer-open')) {
      document.documentElement.classList.remove('filter-drawer-open');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }

    const finishClosing = () => {
      form.hidden = true;
      backdrop.hidden = true;
      this.querySelector('.collection-toolbar')?.classList.remove('is-drawer-open');
    };

    if (immediate) {
      finishClosing();
    } else {
      this.drawerCloseTimer = window.setTimeout(finishClosing, 220);
    }

    if (restoreFocus) {
      openButton.focus();
    }
  }

  toggleAccordion(trigger) {
    const body = this.getAccordionBody(trigger);
    if (!body) {
      return;
    }

    const expanded = trigger.getAttribute('aria-expanded') !== 'true';
    trigger.classList.toggle('accordion-is-open', expanded);
    trigger.setAttribute('aria-expanded', String(expanded));

    if (expanded) {
      this.renderPriceRanges();
    }
  }

  getAccordionBody(trigger) {
    const bodyId = trigger.getAttribute('aria-controls');
    return bodyId ? this.querySelector(`[data-filter-drawer-accordion-body][id="${bodyId}"]`) : null;
  }

  renderPriceRanges() {
    this.querySelectorAll('[data-price-range]').forEach((priceRange) => this.updateRangeFromFields(priceRange));
  }

  getPriceRangeParts(priceRange) {
    const priceContainer = priceRange.closest('.filter__price');
    const minInput = priceContainer?.querySelector('[data-field-price-min]');
    const maxInput = priceContainer?.querySelector('[data-field-price-max]');
    const minRange = priceRange.querySelector('[data-price-range-min]');
    const maxRange = priceRange.querySelector('[data-price-range-max]');
    const track = priceRange.querySelector('[data-price-range-track]');
    const minimum = Number(priceRange.dataset.seMin);
    const maximum = Number(priceRange.dataset.seMax);

    if (!minInput || !maxInput || !minRange || !maxRange || !track || !Number.isFinite(minimum) || !Number.isFinite(maximum)) {
      return null;
    }

    return {maximum, maxInput, maxRange, minimum, minInput, minRange, track};
  }

  clampPrice(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  getPriceInputValue(input, fallback, minimum, maximum) {
    if (input.value.trim() === '') {
      return fallback;
    }

    const value = Number(input.value);
    return Number.isFinite(value) ? this.clampPrice(value, minimum, maximum) : fallback;
  }

  updateRangeFromFields(priceRange, changedInput = null, commitEmpty = false) {
    const parts = this.getPriceRangeParts(priceRange);
    if (!parts) {
      return;
    }

    const minFallback = commitEmpty ? parts.minimum : Number(parts.minRange.value);
    const maxFallback = commitEmpty ? parts.maximum : Number(parts.maxRange.value);
    let lower = this.getPriceInputValue(parts.minInput, minFallback, parts.minimum, parts.maximum);
    let upper = this.getPriceInputValue(parts.maxInput, maxFallback, parts.minimum, parts.maximum);

    if (lower > upper) {
      if (changedInput === parts.minInput) {
        upper = lower;
        parts.maxInput.value = String(upper);
      } else {
        lower = upper;
        parts.minInput.value = String(lower);
      }
    }

    parts.minRange.value = String(lower);
    parts.maxRange.value = String(upper);
    this.renderPriceTrack(priceRange, parts, lower, upper);
  }

  updateFieldsFromRanges(priceRange, changedRange) {
    const parts = this.getPriceRangeParts(priceRange);
    if (!parts) {
      return;
    }

    let lower = this.clampPrice(Number(parts.minRange.value), parts.minimum, parts.maximum);
    let upper = this.clampPrice(Number(parts.maxRange.value), parts.minimum, parts.maximum);

    if (lower > upper) {
      if (changedRange === parts.minRange) {
        upper = lower;
        parts.maxRange.value = String(upper);
      } else {
        lower = upper;
        parts.minRange.value = String(lower);
      }
    }

    parts.minInput.value = String(lower);
    parts.maxInput.value = String(upper);
    this.renderPriceTrack(priceRange, parts, lower, upper);
  }

  renderPriceTrack(priceRange, parts, lower, upper) {
    const availableWidth = Math.max(0, priceRange.clientWidth - 20);
    const valueRange = parts.maximum - parts.minimum || 1;
    const lowerPosition = 10 + ((lower - parts.minimum) / valueRange) * availableWidth;
    const upperPosition = 10 + ((upper - parts.minimum) / valueRange) * availableWidth;

    parts.track.style.left = `${lowerPosition}px`;
    parts.track.style.width = `${Math.max(0, upperPosition - lowerPosition)}px`;
  }

  isPlainLeftClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  shouldHandleLink(link) {
    return (
      link.matches('.filters__drawer-clear, .active__filters__remove, .active__filters__clear, [data-collection-ajax-reset]') ||
      Boolean(link.closest('.pagination'))
    );
  }

  cleanUrl(url) {
    const cleanUrl = new URL(url, window.location.origin);
    cleanUrl.searchParams.delete('sections');
    cleanUrl.searchParams.delete('section_id');
    return cleanUrl;
  }

  getFormUrl(form) {
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const params = new URLSearchParams();

    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') {
        params.append(key, value);
      }
    });

    form.querySelectorAll('[data-price-range]').forEach((priceRange) => {
      const priceContainer = priceRange.closest('.filter__price');
      const minInput = priceContainer?.querySelector('[data-field-price-min]');
      const maxInput = priceContainer?.querySelector('[data-field-price-max]');
      const minimum = Number(priceRange.dataset.seMin);
      const maximum = Number(priceRange.dataset.seMax);

      if (!minInput || !maxInput || !Number.isFinite(minimum) || !Number.isFinite(maximum)) {
        return;
      }

      if (Number(minInput.value) === minimum) {
        params.delete(minInput.name);
      }

      if (Number(maxInput.value) === maximum) {
        params.delete(maxInput.name);
      }
    });

    params.delete('page');
    url.search = params.toString();
    return url;
  }

  setLoading(loading, statusMessage = '') {
    this.classList.toggle('is-loading', loading);
    this.setAttribute('aria-busy', String(loading));
    this.querySelectorAll('.filters__drawer-apply').forEach((button) => {
      button.disabled = loading;
    });
    this.querySelectorAll('[data-collection-sort-toggle], [data-collection-sort-option]').forEach((button) => {
      button.disabled = loading;
    });

    const loader = this.querySelector('[data-collection-loader]');
    const status = this.querySelector('[data-collection-status]');
    loader?.setAttribute('aria-hidden', String(!loading));

    if (status) {
      status.textContent = statusMessage;
    }
  }

  replaceTargets(nextRoot) {
    const selectors = ['[data-collection-active-filters]', '[data-collection-controls]', '[data-collection-results]'];
    const targets = selectors.map((selector) => ({
      current: this.querySelector(selector),
      next: nextRoot.querySelector(selector),
    }));

    if (targets.some(({current, next}) => !current || !next)) {
      throw new Error('Collection update targets are unavailable.');
    }

    targets.forEach(({current, next}) => {
      current.innerHTML = next.innerHTML;
    });
  }

  focusFilterTrigger() {
    this.querySelector('[data-filter-drawer-open]')?.focus({preventScroll: true});
  }

  focusSortToggle() {
    this.querySelector('[data-collection-sort-toggle]')?.focus({preventScroll: true});
  }

  scrollToCollectionStart() {
    const target =
      this.querySelector('[data-collection-scroll-anchor]') || this.querySelector('[data-collection-results]');
    if (!target) {
      return;
    }

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const scrollOffset = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    const targetTop = window.scrollY + target.getBoundingClientRect().top - scrollOffset;
    window.scrollTo({top: Math.max(0, targetTop), behavior});
  }

  async navigate(destination, options = {}) {
    if (!this.sectionId) {
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;
    const displayUrl = this.cleanUrl(destination);
    const requestUrl = new URL(displayUrl);
    requestUrl.searchParams.set('sections', this.sectionId);
    let errorMessage = '';

    this.setLoading(true, this.dataset.loadingMessage || '');

    try {
      const response = await window.fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Collection section request failed with status ${response.status}.`);
      }

      const sections = await response.json();
      if (requestId !== this.requestId) {
        return;
      }

      const responseHtml = sections[this.sectionId];
      if (!responseHtml) {
        throw new Error('Collection section markup is unavailable.');
      }

      const documentFragment = new DOMParser().parseFromString(responseHtml, 'text/html');
      const nextRoot = Array.from(documentFragment.querySelectorAll('[data-collection-ajax]')).find(
        (element) => element.dataset.sectionId === this.sectionId,
      );

      if (!nextRoot) {
        throw new Error('Collection section root is unavailable.');
      }

      this.replaceTargets(nextRoot);
      this.initializeUi();

      if (options.updateHistory !== false) {
        window.history.pushState({}, '', displayUrl.toString());
      }

      this.currentUrl = displayUrl.toString();

      if (options.focusFilterTrigger) {
        this.focusFilterTrigger();
      }

      if (options.focusSortToggle) {
        this.focusSortToggle();
      }

      if (options.scrollToCollectionStart) {
        this.scrollToCollectionStart();
      }

      this.dispatchEvent(new CustomEvent('collection:updated', {bubbles: true, detail: {url: this.currentUrl}}));
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      errorMessage = this.dataset.errorMessage || '';
      if (options.updateHistory === false && this.currentUrl) {
        window.history.replaceState({}, '', this.currentUrl);
      }

      if (options.focusFilterTrigger) {
        this.focusFilterTrigger();
      }

      if (options.focusSortToggle) {
        this.focusSortToggle();
      }

      console.error(error);
    } finally {
      if (requestId === this.requestId) {
        this.setLoading(false, errorMessage);
      }
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
